import { motion } from "framer-motion";
import { ArrowRight, MapPin, Sparkles } from "lucide-react";
import { LOGO_URL, COMPANY, GALLERY } from "../data/content";

export default function Hero() {
    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative bg-haas-cream pt-36 sm:pt-44 pb-20 sm:pb-28 overflow-hidden"
        >
            {/* Decorative blobs */}
            <div className="pointer-events-none absolute -top-20 -right-24 w-[420px] h-[420px] rounded-full bg-haas-sun/40 blur-3xl" />
            <div className="pointer-events-none absolute top-1/2 -left-20 w-[360px] h-[360px] rounded-full bg-haas-gold/30 blur-3xl" />

            <div className="relative max-w-7xl mx-auto px-5 sm:px-10">
                {/* Top chip */}
                <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex justify-center mb-10 sm:mb-14"
                >
                    <span className="chip" data-testid="hero-chip">
                        <Sparkles className="w-3.5 h-3.5 text-haas-goldDeep" />
                        Coventry landscapers · Free quotes
                    </span>
                </motion.div>

                {/* Logo — big and proud */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.85, rotate: -3 }}
                    animate={{ opacity: 1, scale: 1, rotate: 0 }}
                    transition={{
                        duration: 0.8,
                        type: "spring",
                        stiffness: 80,
                    }}
                    className="flex justify-center mb-10"
                    data-testid="hero-logo-wrap"
                >
                    <img
                        src={LOGO_URL}
                        alt="Luxe Artificial Grass Ltd"
                        className="logo-pop w-56 sm:w-72 lg:w-96 h-auto rounded-[2rem] border-[3px] border-haas-ink shadow-[12px_12px_0_#123D28]"
                        data-testid="hero-logo-img"
                    />
                </motion.div>

                {/* Headline */}
                <motion.h1
                    data-testid="hero-headline"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    className="font-display font-bold text-center text-5xl sm:text-7xl lg:text-8xl leading-[1] tracking-tight text-haas-ink max-w-5xl mx-auto"
                >
                    Greener gardens.{" "}
                    <span className="marker-highlight">sharper</span> patios.{" "}
                    <span className="font-hand text-haas-goldDeep text-6xl sm:text-8xl lg:text-9xl inline-block -rotate-2 align-middle">
                        better summers.
                    </span>
                </motion.h1>

                {/* Subline */}
                <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.35 }}
                    className="mt-8 text-center text-lg sm:text-xl text-haas-soft max-w-2xl mx-auto leading-relaxed"
                >
                    We're <span className="font-semibold text-haas-ink">Luxe Artificial Grass</span> — a Coventry-based landscaping team laying{" "}
                    <span className="squiggle font-semibold text-haas-ink">artificial grass</span>, patios, driveways and{" "}
                    <span className="squiggle font-semibold text-haas-ink">fencing</span>, properly. No drama, no surprises, just lovely work.
                </motion.p>

                {/* CTAs */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.5 }}
                    className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
                >
                    <a
                        href="#contact"
                        data-testid="hero-primary-cta"
                        className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-haas-ink text-haas-cream hover:bg-haas-goldDeep hover:text-haas-ink rounded-full text-sm font-bold transition-colors shadow-[6px_6px_0_#D4AF37]"
                    >
                        Get my free quote
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </a>
                    <a
                        href={`tel:${COMPANY.phoneTel}`}
                        data-testid="hero-secondary-cta"
                        className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-haas-ink rounded-full text-sm font-bold border-2 border-haas-ink hover:bg-haas-sun transition-colors shadow-[6px_6px_0_#123D28]"
                    >
                        Call {COMPANY.phone}
                    </a>
                </motion.div>

                {/* Location strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.7, delay: 0.6 }}
                    className="mt-8 flex items-center justify-center gap-2 text-sm text-haas-soft"
                >
                    <MapPin className="w-4 h-4 text-haas-goldDeep" />
                    West Midlands &amp; surrounding areas
                </motion.div>

                {/* Recent gardens preview */}
                <motion.div
                    id="gallery"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                    className="mt-16 sm:mt-20 scroll-mt-32"
                    data-testid="hero-gardens-preview"
                >
                    <div className="text-center mb-8">
                        <span className="font-hand text-haas-goldDeep text-3xl sm:text-4xl inline-block -rotate-1">
                            Recent gardens we've transformed
                        </span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        {GALLERY.map((g, i) => (
                            <a
                                key={g.id}
                                href="#summer-offer"
                                data-testid={`hero-garden-${g.id}`}
                                aria-label={g.title}
                                className={`group block overflow-hidden rounded-2xl border-2 border-haas-ink shadow-[6px_6px_0_#123D28] hover:shadow-[3px_3px_0_#123D28] hover:translate-x-[3px] hover:translate-y-[3px] transition-all ${
                                    i % 2 === 0 ? "" : "sm:translate-y-3"
                                }`}
                            >
                                <div className="relative aspect-square">
                                    <img
                                        src={g.src}
                                        alt={g.title}
                                        loading="lazy"
                                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                </div>
                            </a>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
